/**
 * Adaptive color system: derive an entire theme from a single background color.
 *
 * Pipeline: hex → sRGB → linear RGB → XYZ (D65) → OKLAB → OKLCH
 * All derived tokens are computed in OKLCH for perceptual uniformity,
 * then converted back to hex for CSS custom properties.
 */

// ── Color-space conversions ──────────────────────────────────────────

function hexToSrgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16) / 255,
    parseInt(h.slice(2, 4), 16) / 255,
    parseInt(h.slice(4, 6), 16) / 255,
  ];
}

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function linearToSrgb(c: number): number {
  c = Math.max(0, Math.min(1, c));
  return c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055;
}

function linearRgbToOklab(r: number, g: number, b: number): [number, number, number] {
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
  const l_ = Math.cbrt(l),
    m_ = Math.cbrt(m),
    s_ = Math.cbrt(s);
  return [
    0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
    1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
  ];
}

function oklabToLinearRgb(L: number, a: number, b: number): [number, number, number] {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;
  const l = l_ * l_ * l_,
    m = m_ * m_ * m_,
    s = s_ * s_ * s_;
  return [
    +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

function oklchToOklab(L: number, C: number, H: number): [number, number, number] {
  const hRad = (H * Math.PI) / 180;
  return [L, C * Math.cos(hRad), C * Math.sin(hRad)];
}

function oklabToOklch(L: number, a: number, b: number): [number, number, number] {
  const C = Math.sqrt(a * a + b * b);
  let H = (Math.atan2(b, a) * 180) / Math.PI;
  if (H < 0) H += 360;
  return [L, C, H];
}

function oklchToHex(L: number, C: number, H: number): string {
  const [labL, labA, labB] = oklchToOklab(L, C, H);
  const [lr, lg, lb] = oklabToLinearRgb(labL, labA, labB);
  const r = Math.round(linearToSrgb(lr) * 255);
  const g = Math.round(linearToSrgb(lg) * 255);
  const b = Math.round(linearToSrgb(lb) * 255);
  return (
    "#" + [r, g, b].map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0")).join("")
  );
}

function hexToOklch(hex: string): [number, number, number] {
  const [sr, sg, sb] = hexToSrgb(hex);
  const [lr, lg, lb] = [srgbToLinear(sr), srgbToLinear(sg), srgbToLinear(sb)];
  const [labL, labA, labB] = linearRgbToOklab(lr, lg, lb);
  return oklabToOklch(labL, labA, labB);
}

// ── Contrast ─────────────────────────────────────────────────────────

const HEX6 = /^#[0-9a-f]{6}$/i;

/** True for a "#rrggbb" string — the only color form the theme pipeline accepts. */
export function isHex6(value: unknown): value is string {
  return typeof value === "string" && HEX6.test(value);
}

/** WCAG 2.x relative luminance of a hex color. */
function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToSrgb(hex).map(srgbToLinear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * WCAG 2.x contrast ratio between two hex colors (1 → 21).
 * AA body text needs ≥ 4.5, large text and UI components ≥ 3.
 */
export function contrastRatio(hexA: string, hexB: string): number {
  const a = relativeLuminance(hexA);
  const b = relativeLuminance(hexB);
  const [hi, lo] = a > b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

// ── Helpers ──────────────────────────────────────────────────────────

/** Clamp lightness to [0, 1] and chroma to [0, 0.4] */
function lch(L: number, C: number, H: number): [number, number, number] {
  return [Math.max(0, Math.min(1, L)), Math.max(0, Math.min(0.4, C)), ((H % 360) + 360) % 360];
}

/** Shift lightness from a base, preserving chroma and hue */
function shiftL(base: [number, number, number], delta: number, chromaScale = 1): string {
  return oklchToHex(...lch(base[0] + delta, base[1] * chromaScale, base[2]));
}

// ── Theme derivation ─────────────────────────────────────────────────

/**
 * Where black text and white text give equal WCAG contrast (Y ≈ 0.179).
 * Below it, light text always wins; above it, dark text always wins. This is
 * the only correct light/dark split for text — an OKLCH-lightness cutoff
 * misclassifies the whole mid-tone band (slate-500, red-600, indigo-600…),
 * which is how "dark text on a dark card" used to happen.
 */
const LUMINANCE_CROSSOVER = 0.179;

/** True when light text reads better than dark text on this background. */
export function isDarkBackground(bgHex: string): boolean {
  return relativeLuminance(bgHex) < LUMINANCE_CROSSOVER;
}

/** Contrast targets per text tier. Primary/secondary are WCAG AA for body
 *  text; tertiary is AA for large text and UI; muted is decorative. */
const TEXT_TARGETS = { primary: 7, secondary: 4.5, tertiary: 3, muted: 2.5 } as const;

/**
 * Start from a designed lightness and push it away from the background, 1%
 * at a time, until the color clears each reference surface's target (or hits
 * pure black/white, which is the most contrast there is).
 * Backgrounds that already satisfy the target keep the designed value, so
 * the default light and dark themes are pixel-identical to before.
 */
type ContrastRef = [hex: string, target: number];

function solveContrast(
  designL: number,
  C: number,
  H: number,
  refs: ContrastRef[],
  lighten: boolean
): string {
  let L = designL;
  let hex = oklchToHex(...lch(L, C, H));
  const meets = (h: string) => refs.every(([ref, target]) => contrastRatio(ref, h) >= target);
  for (let i = 0; i < 100 && !meets(hex); i++) {
    const next = lighten ? Math.min(1, L + 0.01) : Math.max(0, L - 0.01);
    if (next === L) break;
    L = next;
    hex = oklchToHex(...lch(L, C, H));
  }
  return hex;
}

export interface DerivedTheme {
  "--color-bg": string;
  "--color-bg-secondary": string;
  "--color-bg-hover": string;
  "--color-text-primary": string;
  "--color-text-secondary": string;
  "--color-text-tertiary": string;
  "--color-text-muted": string;
  "--color-text-inverse": string;
  "--color-border": string;
  "--color-border-subtle": string;
  "--color-border-hover": string;
  "--color-danger": string;
  "--color-success": string;
  "--color-warning": string;
  "--card-glass": string;
  "--card-glow": string;
  "--card-shadow": string;
}

/**
 * Derive a full set of CSS color tokens from a single background hex color.
 *
 * Every text tier is solved against both the page background and the
 * floating surface (cards, modals) so it meets its WCAG target on either;
 * semantic colors are solved to at least 3:1 against the page. Neutral
 * lightness values below are the *designed* starting points — they hold
 * on the default themes and only move when a background forces them to.
 */
export function deriveTheme(bgHex: string): DerivedTheme {
  const base = hexToOklch(bgHex);
  const [L, C, H] = base;
  const isDark = isDarkBackground(bgHex);

  // Tint factor: keep a whisper of the base hue in neutrals
  const tintC = Math.min(C * 0.15, 0.01);

  // ── Backgrounds ──
  // secondary is a surface that "floats" above the bg (cards, modals)
  // hover is the interactive highlight
  // On a dark page the surface normally lifts. But a mid-tone page is already
  // near the luminance where even pure white text barely passes AA, so lifting
  // the surface further would make cards unreadable — those sink instead.
  const white = oklchToHex(...lch(1, 0, 0));
  const lift = (delta: number, chroma: number) => {
    const lifted = shiftL(base, delta, chroma);
    return contrastRatio(lifted, white) >= TEXT_TARGETS.secondary
      ? lifted
      : shiftL(base, -delta, chroma);
  };
  const bgSecondary = isDark ? lift(0.04, 0.5) : shiftL(base, 0.02, 0.5);
  const bgHover = isDark ? lift(0.08, 0.4) : shiftL(base, -0.04, 0.4);

  // ── Text hierarchy ──
  // Text sits on the page, on cards and on hovered rows, so it has to clear
  // the target on whichever of those surfaces is closest to it in luminance.
  const surfaces = [bgHex, bgSecondary, bgHover];
  const text = (light: number, dark: number, target: number) =>
    solveContrast(
      isDark ? light : dark,
      tintC,
      H,
      surfaces.map((s): ContrastRef => [s, target]),
      isDark
    );
  const textPrimary = text(0.95, 0.13, TEXT_TARGETS.primary);
  const textSecondary = text(0.72, 0.38, TEXT_TARGETS.secondary);
  const textTertiary = text(0.58, 0.5, TEXT_TARGETS.tertiary);
  const textMuted = text(0.42, 0.63, TEXT_TARGETS.muted);
  const textInverse = oklchToHex(...lch(isDark ? 0.13 : 0.95, tintC, H));

  // ── Borders ──
  // Cinematic dark (L < 0.2): very subtle borders for glass-like feel
  const isCinematicBorder = isDark && L < 0.2;
  const border = oklchToHex(
    ...lch(
      isDark ? L + (isCinematicBorder ? 0.06 : 0.1) : L - 0.08,
      tintC * (isCinematicBorder ? 1 : 2),
      H
    )
  );
  const borderSubtle = oklchToHex(
    ...lch(isDark ? L + (isCinematicBorder ? 0.03 : 0.05) : L - 0.03, tintC, H)
  );
  const borderHover = oklchToHex(
    ...lch(
      isDark ? L + (isCinematicBorder ? 0.1 : 0.16) : L - 0.14,
      tintC * (isCinematicBorder ? 1 : 2),
      H
    )
  );

  // ── Semantic colors ──
  // Designed to pop on the defaults; pushed further on backgrounds that
  // share their hue or luminance (a red-600 page must still show "danger").
  const semantic = (light: number, dark: number, chroma: number, hue: number) =>
    solveContrast(isDark ? light : dark, chroma, hue, [[bgHex, 3]], isDark);
  const danger = semantic(0.7, 0.48, 0.19, 25);
  const success = semantic(0.7, 0.48, 0.17, 155);
  const warning = semantic(0.8, 0.55, 0.16, 85);

  // Cinematic card effects — glass & glow for dark, clean for light
  const isCinematic = isDark && L < 0.2; // very dark backgrounds get cinematic treatment
  const cardGlass = isCinematic ? `rgba(255,255,255,0.03)` : "none";
  const cardGlow = isCinematic
    ? `0 0 20px rgba(255,255,255,0.02), inset 0 1px 0 rgba(255,255,255,0.05)`
    : "none";
  const cardShadow = isCinematic
    ? `0 4px 24px rgba(0,0,0,0.4), 0 1px 3px rgba(0,0,0,0.3)`
    : isDark
      ? `0 1px 3px rgba(0,0,0,0.2)`
      : "none";

  return {
    "--color-bg": bgHex,
    "--color-bg-secondary": bgSecondary,
    "--color-bg-hover": bgHover,
    "--color-text-primary": textPrimary,
    "--color-text-secondary": textSecondary,
    "--color-text-tertiary": textTertiary,
    "--color-text-muted": textMuted,
    "--color-text-inverse": textInverse,
    "--color-border": border,
    "--color-border-subtle": borderSubtle,
    "--color-border-hover": borderHover,
    "--color-danger": danger,
    "--color-success": success,
    "--color-warning": warning,
    "--card-glass": cardGlass,
    "--card-glow": cardGlow,
    "--card-shadow": cardShadow,
  };
}

/**
 * Tokens for a single surface (a card, a badge) whose background differs from
 * the page — e.g. a link card with a user-picked color. The page's text tokens
 * are derived from the page background, so they can vanish on a card of the
 * opposite luminance. Spread the result into the element's inline `style`:
 * every child that reads `var(--color-text-*)` then inherits readable values.
 *
 * Returns null for anything that isn't a 6-digit hex, so callers can fall
 * back to the page tokens without guarding.
 */
export type SurfaceTokens = Pick<
  DerivedTheme,
  | "--color-text-primary"
  | "--color-text-secondary"
  | "--color-text-tertiary"
  | "--color-text-muted"
  | "--color-text-inverse"
  | "--color-border"
  | "--color-border-hover"
  | "--color-bg-hover"
>;

export function deriveSurfaceTokens(bgHex: string): SurfaceTokens | null {
  if (!isHex6(bgHex)) return null;
  const t = deriveTheme(bgHex);
  return {
    "--color-text-primary": t["--color-text-primary"],
    "--color-text-secondary": t["--color-text-secondary"],
    "--color-text-tertiary": t["--color-text-tertiary"],
    "--color-text-muted": t["--color-text-muted"],
    "--color-text-inverse": t["--color-text-inverse"],
    "--color-border": t["--color-border"],
    "--color-border-hover": t["--color-border-hover"],
    "--color-bg-hover": t["--color-bg-hover"],
  };
}

/** The palette names `paletteIdx` indexes into. Order is load-bearing —
 *  persisted indices must keep resolving to the same palette, so append
 *  only, never reorder or remove. */
export const ACCENT_PALETTES = [
  "sky",
  "emerald",
  "violet",
  "amber",
  "rose",
  "chalk",
  "bone",
  "mist",
  "graphite",
  "moss",
  "plum",
] as const;

// Accent colors that adapt to background luminance
const ACCENT_LIGHT: Record<string, string> = {
  sky: "#0369a1",
  emerald: "#047857",
  violet: "#6d28d9",
  amber: "#b45309",
  rose: "#be123c",
  chalk: "#27485f",
  bone: "#7a4b38",
  mist: "#2f5d5b",
  graphite: "#1d4e5e",
  moss: "#3f5a33",
  plum: "#6b3f5c",
};
const ACCENT_DARK: Record<string, string> = {
  sky: "#38bdf8",
  emerald: "#34d399",
  violet: "#a78bfa",
  amber: "#fbbf24",
  rose: "#fb7185",
  chalk: "#7fb3cf",
  bone: "#d9a384",
  mist: "#7fc0bc",
  graphite: "#8fb8c4",
  moss: "#a3be8c",
  plum: "#c09bb5",
};

/**
 * The accent for a palette on a given background. Starts from the designed
 * light/dark swatch and, when the background would swallow it (a sky page
 * with the sky accent), pushes it away from the background until it clears
 * 3:1 — the minimum for links, focus rings and button fills — and until a
 * `--color-text-inverse` label on an accent-filled button clears 4.5:1.
 */
export function deriveAccent(bgHex: string, palette: string): string {
  const isDark = isDarkBackground(bgHex);
  const swatch =
    (isDark ? ACCENT_DARK : ACCENT_LIGHT)[palette] ?? (isDark ? ACCENT_DARK : ACCENT_LIGHT).sky;
  const [L, C, H] = hexToOklch(swatch);
  const inverse = deriveTheme(bgHex)["--color-text-inverse"];
  return solveContrast(
    L,
    C,
    H,
    [
      [bgHex, 3],
      [inverse, 4.5],
    ],
    isDark
  );
}

/**
 * Apply a derived theme to the document root as CSS custom properties.
 */
export function applyTheme(bgHex: string): void {
  const tokens = deriveTheme(bgHex);
  const root = document.documentElement;
  for (const [prop, value] of Object.entries(tokens)) {
    root.style.setProperty(prop, value);
  }

  // Adapt accent palette to background luminance
  const isDark = isDarkBackground(bgHex);
  const palette = root.getAttribute("data-palette") || "sky";
  root.style.setProperty("--color-accent", deriveAccent(bgHex, palette));

  // Native controls (select popups, date pickers, scrollbars, the default
  // <input> background) follow color-scheme, not our tokens. Without this a
  // dark theme gets white input boxes with near-white inherited text.
  root.style.colorScheme = isDark ? "dark" : "light";

  // Remove the old dark class — it's no longer needed
  root.classList.remove("dark");
}

/** Re-exported for accent adaptation outside the theme pipeline */
export { hexToOklch };

// ── Tailwind color palette ────────────────────────────────────────────
// Standard Tailwind CSS color families, 11 shades each (50–950).
// Organized as rows by hue family for a clean grid layout.

export const TAILWIND_SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

const TAILWIND_FAMILIES: Record<string, string[]> = {
  slate: [
    "#f8fafc",
    "#f1f5f9",
    "#e2e8f0",
    "#cbd5e1",
    "#94a3b8",
    "#64748b",
    "#475569",
    "#334155",
    "#1e293b",
    "#0f172a",
    "#020617",
  ],
  stone: [
    "#fafaf9",
    "#f5f5f4",
    "#e7e5e4",
    "#d6d3d1",
    "#a8a29e",
    "#78716c",
    "#57534e",
    "#44403c",
    "#292524",
    "#1c1917",
    "#0c0a09",
  ],
  red: [
    "#fef2f2",
    "#fee2e2",
    "#fecaca",
    "#fca5a5",
    "#f87171",
    "#ef4444",
    "#dc2626",
    "#b91c1c",
    "#991b1b",
    "#7f1d1d",
    "#450a0a",
  ],
  orange: [
    "#fff7ed",
    "#ffedd5",
    "#fed7aa",
    "#fdba74",
    "#fb923c",
    "#f97316",
    "#ea580c",
    "#c2410c",
    "#9a3412",
    "#7c2d12",
    "#431407",
  ],
  amber: [
    "#fffbeb",
    "#fef3c7",
    "#fde68a",
    "#fcd34d",
    "#fbbf24",
    "#f59e0b",
    "#d97706",
    "#b45309",
    "#92400e",
    "#78350f",
    "#451a03",
  ],
  yellow: [
    "#fefce8",
    "#fef9c3",
    "#fef08a",
    "#fde047",
    "#facc15",
    "#eab308",
    "#ca8a04",
    "#a16207",
    "#854d0e",
    "#713f12",
    "#422006",
  ],
  lime: [
    "#f7fee7",
    "#ecfccb",
    "#d9f99d",
    "#bef264",
    "#a3e635",
    "#84cc16",
    "#65a30d",
    "#4d7c0f",
    "#3f6212",
    "#365314",
    "#1a2e05",
  ],
  green: [
    "#f0fdf4",
    "#dcfce7",
    "#bbf7d0",
    "#86efac",
    "#4ade80",
    "#22c55e",
    "#16a34a",
    "#15803d",
    "#166534",
    "#14532d",
    "#052e16",
  ],
  emerald: [
    "#ecfdf5",
    "#d1fae5",
    "#a7f3d0",
    "#6ee7b7",
    "#34d399",
    "#10b981",
    "#059669",
    "#047857",
    "#065f46",
    "#064e3b",
    "#022c22",
  ],
  teal: [
    "#f0fdfa",
    "#ccfbf1",
    "#99f6e4",
    "#5eead4",
    "#2dd4bf",
    "#14b8a6",
    "#0d9488",
    "#0f766e",
    "#115e59",
    "#134e4a",
    "#042f2e",
  ],
  cyan: [
    "#ecfeff",
    "#cffafe",
    "#a5f3fc",
    "#67e8f9",
    "#22d3ee",
    "#06b6d4",
    "#0891b2",
    "#0e7490",
    "#155e75",
    "#164e63",
    "#083344",
  ],
  sky: [
    "#f0f9ff",
    "#e0f2fe",
    "#bae6fd",
    "#7dd3fc",
    "#38bdf8",
    "#0ea5e9",
    "#0284c7",
    "#0369a1",
    "#075985",
    "#0c4a6e",
    "#082f49",
  ],
  blue: [
    "#eff6ff",
    "#dbeafe",
    "#bfdbfe",
    "#93c5fd",
    "#60a5fa",
    "#3b82f6",
    "#2563eb",
    "#1d4ed8",
    "#1e40af",
    "#1e3a8a",
    "#172554",
  ],
  indigo: [
    "#eef2ff",
    "#e0e7ff",
    "#c7d2fe",
    "#a5b4fc",
    "#818cf8",
    "#6366f1",
    "#4f46e5",
    "#4338ca",
    "#3730a3",
    "#312e81",
    "#1e1b4b",
  ],
  violet: [
    "#f5f3ff",
    "#ede9fe",
    "#ddd6fe",
    "#c4b5fd",
    "#a78bfa",
    "#8b5cf6",
    "#7c3aed",
    "#6d28d9",
    "#5b21b6",
    "#4c1d95",
    "#2e1065",
  ],
  purple: [
    "#faf5ff",
    "#f3e8ff",
    "#e9d5ff",
    "#d8b4fe",
    "#c084fc",
    "#a855f7",
    "#9333ea",
    "#7e22ce",
    "#6b21a8",
    "#581c87",
    "#3b0764",
  ],
  fuchsia: [
    "#fdf4ff",
    "#fae8ff",
    "#f5d0fe",
    "#f0abfc",
    "#e879f9",
    "#d946ef",
    "#c026d3",
    "#a21caf",
    "#86198f",
    "#701a75",
    "#4a044e",
  ],
  pink: [
    "#fdf2f8",
    "#fce7f3",
    "#fbcfe8",
    "#f9a8d4",
    "#f472b6",
    "#ec4899",
    "#db2777",
    "#be185d",
    "#9d174d",
    "#831843",
    "#500724",
  ],
  rose: [
    "#fff1f2",
    "#ffe4e6",
    "#fecdd3",
    "#fda4af",
    "#fb7185",
    "#f43f5e",
    "#e11d48",
    "#be123c",
    "#9f1239",
    "#881337",
    "#4c0519",
  ],
};

export const TAILWIND_FAMILY_NAMES = Object.keys(TAILWIND_FAMILIES);

export const COLOR_PRESETS: string[] = Object.values(TAILWIND_FAMILIES).flat();

// Cinematic dark presets — deep blacks, navy, charcoal with rich undertones
export const CINEMATIC_PRESETS = [
  {
    color: "#050505",
    label: "Void",
    gradient: "linear-gradient(135deg, #050505 0%, #0a0a0a 100%)",
  },
  { color: "#0a0a14", label: "Ink", gradient: "linear-gradient(135deg, #0a0a14 0%, #10101e 100%)" },
  {
    color: "#0c1222",
    label: "Navy",
    gradient: "linear-gradient(135deg, #0c1222 0%, #0f172a 100%)",
  },
  {
    color: "#0f0f0f",
    label: "Carbon",
    gradient: "linear-gradient(135deg, #0f0f0f 0%, #171717 100%)",
  },
  {
    color: "#0d1117",
    label: "Onyx",
    gradient: "linear-gradient(135deg, #0d1117 0%, #161b22 100%)",
  },
  {
    color: "#1a0a1e",
    label: "Plum",
    gradient: "linear-gradient(135deg, #1a0a1e 0%, #2e1065 100%)",
  },
  {
    color: "#0a1a1a",
    label: "Deep Teal",
    gradient: "linear-gradient(135deg, #0a1a1a 0%, #042f2e 100%)",
  },
] as const;

// Extreme presets: only lightest (50) and darkest (900/950) Tailwind shades
export const EXTREME_PRESETS: string[] = Object.values(TAILWIND_FAMILIES).flatMap((shades) => [
  shades[0],
  shades[shades.length - 1],
]);

// Defaults
export const DEFAULT_LIGHT_BG = "#fafaf9";
export const DEFAULT_DARK_BG = "#09090b";
